import supabase from '../supabase/supabase';

const BUCKET = 'receipts';
const MAX_FILE_SIZE = 10 * 1024 * 1024;

const ALLOWED_TYPES = [
  'application/pdf',
  'image/jpeg',
  'image/png'
];

const db = () => supabase.schema('accounting');

const check = ({ data, error }) => {
  if (error) throw error;
  return data;
};

const validateFile = (file) => {
  if (!file) {
    throw new Error('Please select a receipt file.');
  }

  if (!ALLOWED_TYPES.includes(file.type)) {
    throw new Error('Only PDF, JPG, and PNG files are supported.');
  }

  if (file.size === 0 || file.size > MAX_FILE_SIZE) {
    throw new Error('Receipt files must be between 1 byte and 10 MB.');
  }
};

const safeFilename = (name) => {
  return name
    .replace(/[^a-zA-Z0-9._-]/g, '_')
    .slice(-150);
};

export const receiptService = {
  async getReceipts(organizationId) {
    if (!organizationId) return [];

    return check(
      await db()
        .from('receipts')
        .select('*')
        .eq('organization_id', organizationId)
        .order('created_at', { ascending: false })
    ) || [];
  },

  async getReceipt(organizationId, receiptId) {
    return check(
      await db()
        .from('receipts')
        .select('*')
        .eq('organization_id', organizationId)
        .eq('id', receiptId)
        .single()
    );
  },

  async uploadReceipt(organizationId, file) {
    if (!organizationId) {
      throw new Error('Select a business before uploading.');
    }

    validateFile(file);

    const receiptId = crypto.randomUUID();

    const filePath = [
      organizationId,
      receiptId,
      safeFilename(file.name)
    ].join('/');

    const { data: userData, error: userError } =
      await supabase.auth.getUser();

    if (userError) throw userError;

    const userId = userData?.user?.id;

    if (!userId) {
      throw new Error('You must be signed in to upload receipts.');
    }

    const { error: uploadError } = await supabase.storage
      .from(BUCKET)
      .upload(filePath, file, {
        contentType: file.type,
        upsert: false
      });

    if (uploadError) throw uploadError;

    try {
      return check(
        await db()
          .from('receipts')
          .insert({
            id: receiptId,
            organization_id: organizationId,
            file_name: file.name,
            file_path: filePath,
            file_type: file.type,
            file_size: file.size,
            created_by: userId
          })
          .select()
          .single()
      );
    } catch (error) {
      // Best-effort cleanup if database insert fails.
      try {
        await supabase.storage
          .from(BUCKET)
          .remove([filePath]);
      } catch (cleanupError) {
        console.error(
          'Receipt upload cleanup failed:',
          cleanupError
        );
      }

      throw error;
    }
  },

  async updateReceipt(organizationId, receiptId, updates) {
    const allowedFields = [
      'vendor_id',
      'bill_id',
      'bill_payment_id',
      'receipt_date',
      'amount',
      'currency',
      'notes'
    ];

    const payload = Object.fromEntries(
      Object.entries(updates).filter(([key]) =>
        allowedFields.includes(key)
      )
    );

    return check(
      await db()
        .from('receipts')
        .update(payload)
        .eq('organization_id', organizationId)
        .eq('id', receiptId)
        .select()
        .single()
    );
  },

  async getSignedUrl(filePath) {
    const result = check(
      await supabase.storage
        .from(BUCKET)
        .createSignedUrl(filePath, 60)
    );

    return result.signedUrl;
  },

  async deleteReceipt(organizationId, receiptId) {
    const receipt = await this.getReceipt(
      organizationId,
      receiptId
    );

    // Remove the file first. If that fails, keep the DB record.
    const { error: storageError } = await supabase.storage
      .from(BUCKET)
      .remove([receipt.file_path]);

    if (storageError) throw storageError;

    // If DB deletion fails, the record may remain without its file.
    // Production cleanup should use a server-side workflow.
    return check(
      await db()
        .from('receipts')
        .delete()
        .eq('organization_id', organizationId)
        .eq('id', receiptId)
        .select()
        .single()
    );
  },

  
  async extractReceipt(organizationId, receiptId) {
    if (!organizationId || !receiptId) {
      throw new Error('Organization and receipt are required.');
    }

    const { data, error } = await supabase.functions.invoke(
      'extract-receipt',
      {
        body: {
          organizationId,
          receiptId
        }
      }
    );

    if (error) {
      let message = error.message || 'AI extraction failed.';

      try {
        const response = error.context;

        if (response && typeof response.json === 'function') {
          const details = await response.json();
          message = details.error || message;
        }
      } catch {
        // Keep the original error message.
      }

      throw new Error(message);
    }

    if (!data?.success) {
      throw new Error(
        data?.error || 'Unable to extract receipt information.'
      );
    }

    return data;
  },

};